// src/components/assistant/ThreadHistory.tsx
//
// Historial de conversaciones del chat, SEPARADO del menú de la app:
//  - ThreadHistoryPanel: «Nuevo chat», búsqueda, lista con renombrar en línea.
//  - HistoryDrawer: en móvil el panel se abre como cajón (no tapa el menú de la
//    app ni pierde el chat: el ChatPane sigue montado detrás).
//  - useHistoryColumn: en escritorio, columna colapsable junto al chat (se
//    recuerda abierta/cerrada por navegador; sin storage, abierta).
// Lo usan /chat (AssistantShell) y el Asistente del shell Orion.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, MessageSquarePlus, PanelLeftClose, Search, X } from 'lucide-react';
import { ThreadListRow } from './ThreadTitle';

export interface HistoryThread {
  id: string;
  title: string;
  hasTitle?: boolean;
  at?: string | null;
}

const fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

function norm(s: string) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

interface PanelProps {
  threads: HistoryThread[];
  loading?: boolean;
  activeId: string | null;
  onNewChat: () => void;
  onSelect: (id: string) => void;
  onRename?: (id: string, title: string) => Promise<void> | void;
  /** Cerrar (cajón móvil) o colapsar (columna de escritorio). */
  onClose?: () => void;
  closeLabel?: string;
  closeIcon?: 'x' | 'collapse';
}

export function ThreadHistoryPanel({
  threads,
  loading = false,
  activeId,
  onNewChat,
  onSelect,
  onRename,
  onClose,
  closeLabel = 'Cerrar historial',
  closeIcon = 'x',
}: PanelProps) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const n = norm(q.trim());
    return n ? threads.filter((t) => norm(t.title).includes(n)) : threads;
  }, [q, threads]);

  return (
    <div className="flex h-full w-full flex-col bg-card text-foreground">
      <div className="flex items-center gap-2 px-3 pb-2 pt-3">
        <h2 className="flex-1 text-sm font-semibold">Conversaciones</h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label={closeLabel}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted"
          >
            {closeIcon === 'x' ? <X size={17} aria-hidden /> : <PanelLeftClose size={16} aria-hidden />}
          </button>
        )}
      </div>
      <div className="space-y-2 px-3 pb-2">
        <button
          type="button"
          onClick={onNewChat}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          <MessageSquarePlus size={16} aria-hidden /> Nuevo chat
        </button>
        <label className="relative block">
          <span className="sr-only">Buscar conversación</span>
          <Search size={15} aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar conversación"
            className="w-full rounded-lg border border-border bg-background py-2 pl-8 pr-2 text-base text-foreground outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring sm:text-sm"
          />
        </label>
      </div>
      <ul className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {loading && <li className="px-2 py-2 text-sm text-muted-foreground">Cargando…</li>}
        {!loading && threads.length === 0 && (
          <li className="px-2 py-2 text-sm text-muted-foreground">Aún no tienes conversaciones. Empieza una nueva.</li>
        )}
        {!loading && threads.length > 0 && shown.length === 0 && (
          <li className="px-2 py-2 text-sm text-muted-foreground">Ninguna conversación coincide con «{q}».</li>
        )}
        {shown.map((t) => (
          <li key={t.id}>
            <ThreadListRow
              title={t.title}
              active={activeId === t.id}
              onSelect={() => onSelect(t.id)}
              onRename={onRename ? (title) => onRename(t.id, title) : undefined}
              icon={<MessageSquare size={15} aria-hidden className="mt-0.5 shrink-0 text-muted-foreground" />}
              subtitle={t.at ? <span className="block text-xs font-normal text-muted-foreground">{fmt.format(new Date(t.at))}</span> : null}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Cajón del historial en móvil. Esc o tocar fuera lo cierra; el foco entra y regresa. */
export function HistoryDrawer({
  open,
  onClose,
  side = 'right',
  children,
}: {
  open: boolean;
  onClose: () => void;
  side?: 'left' | 'right';
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('button, input')?.focus());
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);

  return (
    <div className={`fixed inset-0 z-50 md:hidden ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Historial de conversaciones"
        className={`absolute inset-y-0 ${side === 'right' ? 'right-0' : 'left-0'} w-[86%] max-w-[320px] pt-[env(safe-area-inset-top)] transition-transform duration-300 ease-out motion-reduce:transition-none ${
          open ? 'translate-x-0 shadow-2xl' : side === 'right' ? 'invisible translate-x-full' : 'invisible -translate-x-full'
        } bg-card`}
      >
        {open && children}
      </div>
    </div>
  );
}

const KEY = 'chat.history.column';

/** Columna de historial en escritorio (abierta por defecto) + cajón en móvil. */
export function useHistoryColumn() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  const [columnOpen, setColumnOpen] = useState(() => {
    try {
      return localStorage.getItem(KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const setColumn = (v: boolean) => {
    setColumnOpen(v);
    try {
      localStorage.setItem(KEY, v ? 'open' : 'closed');
    } catch {
      /* sin storage: solo esta visita */
    }
  };
  return {
    isMobile,
    columnOpen,
    drawerOpen,
    /** Botón «Historial»: en móvil abre el cajón; en escritorio colapsa/expande. */
    toggle: () => (isMobile ? setDrawerOpen((v) => !v) : setColumn(!columnOpen)),
    closeDrawer,
    openDrawer: () => setDrawerOpen(true),
    collapseColumn: () => setColumn(false),
    /** Tras elegir/crear un chat en móvil, el cajón se cierra para mostrarlo. */
    afterPick: () => {
      if (isMobile) setDrawerOpen(false);
    },
  };
}
